/**
 * RUM del panel admin — ADR-0012, paso 5.
 *
 * POR QUÉ NO SE REUSA EL REPORTER DEL STOREFRONT (`webVitals.ts`)
 * Ese mide Core Web Vitals, que se disparan UNA vez por carga de página. El panel es una SPA:
 * después del arranque no vuelve a haber una carga, así que LCP y FCP no vuelven a medir nada
 * mientras el equipo trabaja ocho horas adentro. Medir el panel con esas métricas daría un tablero
 * verde sobre una herramienta que se siente lenta — que es exactamente la discusión que este paso
 * viene a terminar.
 *
 * Lo que se mide acá es la **navegación suave**: desde que cambia la ruta hasta que el contenido
 * está en pantalla. Es lo que el founder reportó ("la lista demora en aparecer") y lo único que
 * dice si los pasos 1 a 4 sirvieron.
 *
 * 🪤 Se guarda el PATRÓN de ruta, no el path. Sin normalizar, cada id de pedido sería su propia
 * ruta: mil navegaciones a mil pedidos distintos se verían como mil rutas con una muestra cada una
 * y ningún percentil significaría nada.
 *
 * La telemetría NUNCA puede romper ni frenar la app: todo está envuelto en try/catch, la cola tiene
 * tope y un envío fallido se descarta en vez de reintentarse para siempre.
 */

import { supabase } from '@/lib/supabase';
import { devLog } from '@/lib/utils';

export type MetricaRum =
  | 'nav_soft' | 'nav_hard' | 'LCP' | 'FCP' | 'TTFB' | 'INP'
  /*
   * Avisarle al cliente. Se agregaron después de un día entero discutiendo latencia A OJO: el
   * founder tuvo que cronometrar con el reloj y yo me equivoqué dos veces sobre dónde se iba el
   * tiempo. Un camino que se discute a ojo se arregla a ojo.
   *
   * 🪤 SON DOS PORQUE SON DOS PREGUNTAS. `aviso_borrador` es lo que el operador ESPERA —clic hasta
   * poder escribir—; `aviso_documento` puede tardar más sin molestar, porque mientras tanto ya lee
   * y edita. Con una sola métrica no se distingue «abre lento» de «el documento tarda», que fue
   * exactamente la confusión que costó el día.
   */
  | 'aviso_borrador' | 'aviso_documento'
  /*
   * Los momentos del arranque, medidos desde el inicio de la navegacion. Ver `hitosDelArranque`.
   * Son MOMENTOS, no duraciones: la resta la hace la consulta, no el navegador.
   */
  | 'hito_js' | 'hito_sesion' | 'hito_pedido' | 'hito_datos'
  | 'hito_permisos' | 'hito_chunk' | 'hito_cascaron'
  | 'hito_barra' | 'hito_marco' | 'hito_montado' | 'hito_filas'
  /*
   * Cómo se entera el chat. `chat_msg` mide cuánto tardó un mensaje desde que WhatsApp lo fechó
   * hasta que se dibujó, y la RUTA dice por cuál de los tres caminos llegó; `chat_canal` mide qué
   * le pasó a la conexión en vivo y cuánto vivió.
   *
   * 🚨 SE AGREGARON PORQUE NO SE PODÍA RESPONDER «¿por qué tarda?». Lo único medible era el tramo
   * de afuera —WhatsApp → nuestra base, 3,6 s de mediana— y del tramo de adentro no había un solo
   * dato. Ver `lib/whatsapp/medicionDelChat.ts`.
   *
   * 🪤 EL CHECK DE LA TABLA TAMBIÉN LAS TIENE QUE CONOCER. Agregar el valor sólo acá hace que
   * PostgREST rechace la fila y la telemetría se pierda en silencio, porque un envío fallido se
   * descarta a propósito. Van juntos: este tipo y la migración del CHECK.
   */
  | 'chat_msg' | 'chat_canal'
  /*
   * Lo que tarda la torre de control, medido desde Smarty HQ: cada lectura, la demora del aviso en vivo y la vida de su
   * canal (`lib/torre/medicionDeLaTorre.ts`). La base compara con el objetivo y avisa si no se cumple (20261029004000).
   */
  | 'torre_lectura' | 'torre_aviso' | 'torre_canal';

export interface EventoRum {
  tenant_id: string;
  user_id: string;
  route: string;
  metric: MetricaRum;
  value_ms: number;
  cache_hit: boolean | null;
}

/** Tope de la cola. Si se llena es que los envíos fallan; se tiran los más viejos y se sigue. */
const MAX_COLA = 60;
/** A partir de acá se manda sin esperar el temporizador. */
const LOTE = 20;
const INTERVALO_MS = 20_000;
/** Coherente con el CHECK de la tabla: más que esto es una pestaña que estuvo dormida, no una medición. */
const TOPE_MS = 120_000;

let cola: EventoRum[] = [];
let temporizador: ReturnType<typeof setInterval> | null = null;

const RE_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Convierte un path concreto en el patrón de ruta que se agrega.
 *
 *   /store/dulti/operaciones/pedidos/9f9a…  →  /store/:slug/operaciones/pedidos/:id
 *
 * No usa el árbol de rutas a propósito: `useMatches()` sólo devuelve los patrones con el router de
 * datos (`createBrowserRouter`), que todavía no está montado (paso 2). Normalizar por forma del
 * segmento funciona con cualquiera de los dos y no hay que volver a tocarlo cuando se migre.
 */
export function normalizarRuta(pathname: string): string {
  const partes = pathname.split('/').filter(Boolean);
  const salida = partes.map((seg, i) => {
    // El slug de la tienda es siempre el segmento que sigue a `store`. Es legible y no es un id,
    // así que sin este caso se colaría tal cual y habría una ruta por tienda.
    if (i === 1 && partes[0] === 'store') return ':slug';
    if (RE_UUID.test(seg)) return ':id';
    if (/^\d+$/.test(seg)) return ':n';
    // Códigos de negocio: `S-16651` (pedido), `V-204`, `COD-9912`. Letras, guion y números.
    //
    // 🪤 Se agregó al ver el RUM real: `/apps/operaciones/orden/S-16651` aparecía como su propia
    // ruta. Cada pedido abierto creaba una fila con UNA muestra, así que la pantalla de detalle de
    // pedido —de las más usadas— quedaba invisible en el tablero: nunca juntaba muestras suficientes
    // para tener un p75 que significara algo. El sesgo es el peor posible: la pantalla parece no
    // usarse justo cuando más se usa.
    //
    // 🪤 Y DESDE EL 23-sep EL GUION PUEDE NO ESTAR: el número del pedido pasó a ser una sola
    // pieza (`S16651`, ver 20260923230000). La regla vieja EXIGÍA el guion, así que la forma nueva
    // no matcheaba, tampoco llegaba al umbral de 12 caracteres de abajo, y volvía tal cual — es
    // decir, reaparecía exactamente la trampa que este comentario documenta, en silencio y justo
    // cuando el presupuesto de RUM empezara a mirar la pantalla más usada.
    //
    // Sin guion se piden 3 dígitos o más: `v2`, `h1` y compañía son segmentos de ruta, no códigos.
    if (/^[A-Za-z]{1,4}-\d+$/.test(seg) || /^[A-Za-z]{1,4}\d{3,}$/.test(seg)) return ':id';

    // Ids no-uuid (los de Mercado Libre y Falabella, por ejemplo): largos y con dígitos mezclados.
    //
    // 🪤 El umbral es 12, no 20. Con 20 se colaba `MLPE1234567890ABC` (17) y cada publicación de
    // marketplace habría sido su propia ruta. La regla exige DÍGITOS además del largo, que es lo que
    // separa un id de un nombre de pantalla: `configuracion` tiene 13 caracteres y ninguno.
    if (seg.length >= 12 && /\d/.test(seg)) return ':id';
    return seg;
  });
  return ('/' + salida.join('/')).slice(0, 200);
}

/**
 * 🪤 En desarrollo NO se mide. Vite sirve los módulos sueltos y sin compilar a propósito, así que
 * una navegación tarda varias veces lo que tarda en producción: la primera medición real trajo un
 * arranque de **15,7 s** que no era la app, era el servidor de desarrollo.
 *
 * Mezclar las dos cosas rompe la métrica en la dirección peor: infla el p75 y hace parecer que una
 * mejora no sirvió. Y no alcanza con marcar el entorno en una columna para filtrar después —
 * alguien la va a olvidar en la consulta. Lo que no debe contarse, no se manda.
 *
 * Para medir en local: `npm run build && npm run preview`, que sirve el build real.
 */
const MIDE = import.meta.env.PROD;

/** Encola un evento. Descarta en silencio lo que no tenga sentido medir. */
export function encolarRum(e: EventoRum): void {
  if (!MIDE) return;
  try {
    if (!e.tenant_id || !e.user_id) return;
    if (!Number.isFinite(e.value_ms) || e.value_ms < 0 || e.value_ms > TOPE_MS) return;
    cola.push({ ...e, value_ms: Math.round(e.value_ms) });
    // 🪤 Se tiran los más VIEJOS, no los nuevos: si los envíos están fallando, lo último que pasó es
    // más útil que lo primero, y quedarse con lo primero convertiría la cola en un museo.
    if (cola.length > MAX_COLA) cola = cola.slice(-MAX_COLA);
    if (cola.length >= LOTE) void enviarRum();
  } catch {
    /* la telemetría no rompe nada */
  }
}

/**
 * Manda lo acumulado. Inserta con el cliente autenticado: RLS exige que la fila sea del propio
 * usuario y su propio tenant, así que no hace falta una edge function ni una allowlist de CORS
 * (que es lo que dejó `storefront_web_vitals` en cero filas desde que existe).
 */
export async function enviarRum(): Promise<void> {
  if (cola.length === 0) return;
  const lote = cola;
  cola = [];
  try {
    const { error } = await supabase.from('admin_rum_events').insert(lote);
    // 🪤 Un envío fallido se DESCARTA, no se reintenta. Reencolar haría que un error persistente
    // —una policy mal puesta, por ejemplo— reintente para siempre y gaste red en cada tanda.
    // Perder métricas es aceptable; que la telemetría se vuelva el problema, no.
    if (error) devLog.warn('[panelRum] no se pudo enviar:', error.message);
  } catch (err) {
    devLog.warn('[panelRum] no se pudo enviar:', err);
  }
}

/** Arranca el envío periódico. Idempotente. Devuelve la función para detenerlo. */
export function iniciarEnvioRum(): () => void {
  // En desarrollo no hay nada que mandar (ver `MIDE`), y sin esto quedaría un intervalo despierto
  // cada 20 s en cada recarga en caliente de Vite.
  if (!MIDE) return () => {};
  if (temporizador) return () => {};
  temporizador = setInterval(() => { void enviarRum(); }, INTERVALO_MS);

  // Al ocultar la pestaña se manda lo que haya. Es «mejor esfuerzo»: a diferencia del storefront no
  // se puede usar `sendBeacon`, porque el beacon no lleva cabecera de autorización y PostgREST la
  // exige. Se compensa mandando seguido mientras la pestaña está viva; lo que se pierde es, como
  // mucho, la última tanda de veinte segundos.
  const alOcultar = () => { if (document.visibilityState === 'hidden') void enviarRum(); };
  document.addEventListener('visibilitychange', alOcultar);

  return () => {
    if (temporizador) clearInterval(temporizador);
    temporizador = null;
    document.removeEventListener('visibilitychange', alOcultar);
    void enviarRum();
  };
}

/** Sólo para tests: vacía la cola sin mandar nada. */
export function _vaciarColaRum(): void {
  cola = [];
}

/**
 * Cronometra un tramo del camino «avisarle al cliente» y lo encola.
 *
 * 🪤 NO RECIBE EL RELOJ DE ARRANQUE, LO DEVUELVE. Así el que mide no puede olvidarse de arrancarlo
 * ni pasar un origen equivocado: se llama al principio y se invoca el resultado al final.
 *
 * 🪤 LA RUTA ES EL TIPO DE AVISO. `admin_rum_events.route` guarda patrones, no paths — y acá el
 * patrón útil es cuál de los cuatro avisos fue: `/aviso/guia`, `/aviso/llegada`… Son cuatro valores,
 * así que agregan bien; el id del pedido, en cambio, haría una fila por pedido y ningún percentil
 * significaría nada.
 *
 * 🪤 LA TELEMETRÍA NO PUEDE ROMPER NADA. Si algo falla acá, el operador no se entera: medir es para
 * nosotros, y un aviso que no sale porque falló una métrica sería absurdo.
 */
export function cronometrarAviso(
  tipo: string,
  identidad: { tenant_id: string; user_id: string },
): (metrica: 'aviso_borrador' | 'aviso_documento') => void {
  const desde = performance.now();
  return (metrica) => {
    try {
      encolarRum({
        ...identidad,
        route: `/aviso/${tipo}`,
        metric: metrica,
        value_ms: Math.round(performance.now() - desde),
        cache_hit: null,
      });
    } catch {
      /* medir nunca puede estorbar al trabajo */
    }
  };
}
